import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Pencil, Trash2, Box } from 'lucide-react';
import { usePageConfig } from '../../contexts/PageConfigContext';
import { packagingContainersApi, PackagingContainer, CreatePackagingContainerRequest, UpdatePackagingContainerRequest, MaterialType, MaterialTypeLabels, StackingFamily, StackingFamilyLabels } from '../../services/api';
// import { Permissions } from '../../constants/permissions'; // TODO: Add permissions
// import { usePermission } from '../../hooks/usePermission'; // TODO: Use permissions
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';

export const PackagingContainersPage: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  // const canView = usePermission(Permissions.Items.View); // Placeholder
  // const canCreate = usePermission(Permissions.Items.Create); // Placeholder
  const canView = true;
  const canCreate = true;
  const canEdit = true;
  const canDelete = true;

  const [containers, setContainers] = useState<PackagingContainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingContainer, setEditingContainer] = useState<PackagingContainer | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'model'>('general');
  const [formData, setFormData] = useState<CreatePackagingContainerRequest>({
    name: '',
    code: '',
    lengthCm: 0,
    widthCm: 0,
    heightCm: 0,
    maxWeightKg: undefined,
    materialType: MaterialType.Carton,
    stackingFamily: StackingFamily.Generica,
    tareWeightKg: undefined,
    costEur: undefined,
    requiresSeparatorAbove: false,
    requiresSeparatorBelow: false,
    maxStackWeightKg: undefined,
  });

  // State to track if the current editing item has a model (for UI feedback)
  const [currentModelPath, setCurrentModelPath] = useState<string | undefined>(undefined);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0 && editingContainer) {
       const file = e.target.files[0];
       
       if (!file.name.toLowerCase().endsWith('.glb') && !file.name.toLowerCase().endsWith('.gltf')) {
           alert("Solo se permiten archivos .glb o .gltf");
           return;
       }
       
       try {
           setIsUploading(true);
           const result = await packagingContainersApi.uploadModel(editingContainer.id, file);
           alert("Modelo 3D subido con éxito");
           setCurrentModelPath(result.path);
           loadContainers();
       } catch (err: any) {
           console.error(err);
           alert("Error al subir modelo: " + (err.message || "Error desconocido"));
       } finally {
           setIsUploading(false);
       }
    }
  };

  useEffect(() => {
    setPageConfig({
      title: 'Tipos de Envases',
      actions: canCreate ? [
        {
          label: 'Nuevo Envase',
          icon: Plus,
          onClick: () => {
            setEditingContainer(null);
            setFormData({
              name: '',
              code: '',
              lengthCm: 0,
              widthCm: 0,
              heightCm: 0,
              maxWeightKg: undefined,
              materialType: MaterialType.Carton,
              stackingFamily: StackingFamily.Generica,
              tareWeightKg: undefined,
              costEur: undefined,
              requiresSeparatorAbove: false,
              requiresSeparatorBelow: false,
              maxStackWeightKg: undefined,
            });
            setCurrentModelPath(undefined);
            setActiveTab('general');
            setShowForm(true);
          },
          variant: 'primary' as const
        }
      ] : []
    });
  }, [setPageConfig, canCreate]);

  const loadContainers = async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const data = await packagingContainersApi.getAll();
      setContainers(data);
    } catch (error) {
      alert('Error al cargar envases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContainers();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingContainer) {
        const { code, ...updateData } = formData;
        await packagingContainersApi.update(editingContainer.id, updateData as UpdatePackagingContainerRequest);
        alert('Envase actualizado');
      } else {
        await packagingContainersApi.create(formData);
        alert('Envase creado');
      }
      setShowForm(false);
      loadContainers();
    } catch (error: any) {
      alert(error.message || 'Error al guardar envase');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este envase?')) return;
    try {
      await packagingContainersApi.delete(id);
      alert('Envase eliminado');
      loadContainers();
    } catch (error: any) {
      alert(error.message || 'Error al eliminar');
    }
  };

  const handleEdit = (container: PackagingContainer) => {
    setEditingContainer(container);
    setFormData({
      name: container.name,
      code: container.code,
      lengthCm: container.lengthCm,
      widthCm: container.widthCm,
      heightCm: container.heightCm,
      maxWeightKg: container.maxWeightKg,
      materialType: container.materialType ?? MaterialType.Carton,
      stackingFamily: container.stackingFamily ?? StackingFamily.Generica,
      tareWeightKg: container.tareWeightKg,
      costEur: container.costEur,
      requiresSeparatorAbove: container.requiresSeparatorAbove ?? false,
      requiresSeparatorBelow: container.requiresSeparatorBelow ?? false,
      maxStackWeightKg: container.maxStackWeightKg,
    });
    setCurrentModelPath(container.model3DPath);
    setActiveTab('general');
    setShowForm(true);
  };

  const filteredContainers = containers.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  if (!canView) return <div className="p-6">No tienes permisos para ver envases</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o código..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredContainers.map((container) => (
            <Card key={container.id} className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold">{container.name}</h3>
                  <p className="text-sm text-muted-foreground">Código: {container.code}</p>
                  <p className="text-sm">
                    {container.lengthCm}×{container.widthCm}×{container.heightCm} cm
                    {container.tareWeightKg != null && ` · Tara: ${container.tareWeightKg} kg`}
                    {container.costEur != null && ` · ${container.costEur} €`}
                  </p>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <span className="px-1.5 py-0.5 text-xs rounded bg-blue-50 text-blue-700">
                      {MaterialTypeLabels[container.materialType as MaterialType] ?? 'Sin tipo'}
                    </span>
                    <span className="px-1.5 py-0.5 text-xs rounded bg-purple-50 text-purple-700">
                      {StackingFamilyLabels[container.stackingFamily as StackingFamily] ?? 'Genérica'}
                    </span>
                    {container.requiresSeparatorAbove && (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-amber-50 text-amber-700">Sep. encima</span>
                    )}
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded ${container.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {container.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div className="flex gap-2 justify-end mt-4">
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={() => handleEdit(container)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                )}
                {canDelete && (
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(container.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
      
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingContainer ? 'Editar Envase' : 'Nuevo Envase'}</DialogTitle>
            <DialogDescription>
              Defina las características del envase
            </DialogDescription>
          </DialogHeader>

          <div className="flex border-b mb-4">
            <button
              type="button"
              className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'general' 
                  ? 'border-green-600 text-green-700' 
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
              onClick={() => setActiveTab('general')}
            >
              General
            </button>
            {editingContainer && (
              <button
                type="button"
                className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'model' 
                    ? 'border-green-600 text-green-700' 
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
                onClick={() => setActiveTab('model')}
              >
                Modelo 3D
              </button>
            )}
          </div>

          {activeTab === 'general' ? (
            <form id="packaging-form" onSubmit={handleSubmit}>
                <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                    <Label htmlFor="name">Nombre *</Label>
                    <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                    />
                    </div>
                    <div>
                    <Label htmlFor="code">Código {editingContainer ? '(No editable)' : '*'} </Label>
                    <Input
                        id="code"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        required={!editingContainer}
                        disabled={!!editingContainer}
                        placeholder="BOX-5KG"
                    />
                    </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                    <div>
                    <Label htmlFor="lengthCm">Largo (cm) *</Label>
                    <Input
                        id="lengthCm"
                        type="number"
                        value={formData.lengthCm || ''}
                        onChange={(e) => setFormData({ ...formData, lengthCm: Number(e.target.value) })}
                        required
                    />
                    </div>
                    <div>
                    <Label htmlFor="widthCm">Ancho (cm) *</Label>
                    <Input
                        id="widthCm"
                        type="number"
                        value={formData.widthCm || ''}
                        onChange={(e) => setFormData({ ...formData, widthCm: Number(e.target.value) })}
                        required
                    />
                    </div>
                    <div>
                    <Label htmlFor="heightCm">Alto (cm) *</Label>
                    <Input
                        id="heightCm"
                        type="number"
                        value={formData.heightCm || ''}
                        onChange={(e) => setFormData({ ...formData, heightCm: Number(e.target.value) })}
                        required
                    />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                    <Label htmlFor="materialType">Material</Label>
                    <Select
                        value={String(formData.materialType ?? MaterialType.Carton)}
                        onValueChange={(v) => setFormData({ ...formData, materialType: Number(v) as MaterialType })}
                    >
                        <SelectTrigger>
                        <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                        {Object.entries(MaterialTypeLabels).map(([val, label]) => (
                            <SelectItem key={val} value={val}>{label}</SelectItem>
                        ))}
                        </SelectContent>
                    </Select>
                    </div>
                    <div>
                    <Label htmlFor="stackingFamily">Familia de encaje</Label>
                    <Select
                        value={String(formData.stackingFamily ?? StackingFamily.Generica)}
                        onValueChange={(v) => setFormData({ ...formData, stackingFamily: Number(v) as StackingFamily })}
                    >
                        <SelectTrigger>
                        <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                        {Object.entries(StackingFamilyLabels).map(([val, label]) => (
                            <SelectItem key={val} value={val}>{label}</SelectItem>
                        ))}
                        </SelectContent>
                    </Select>
                    </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                    <div>
                    <Label htmlFor="tareWeightKg">Tara (kg)</Label>
                    <Input
                        id="tareWeightKg"
                        type="number"
                        step="0.001"
                        value={formData.tareWeightKg ?? ''}
                        onChange={(e) => setFormData({ ...formData, tareWeightKg: e.target.value ? Number(e.target.value) : undefined })}
                    />
                    <p className="text-xs text-slate-400 mt-1">Peso caja vacía</p>
                    </div>
                    <div>
                    <Label htmlFor="maxWeightKg">Peso máx contenido (kg)</Label>
                    <Input
                        id="maxWeightKg"
                        type="number"
                        value={formData.maxWeightKg || ''}
                        onChange={(e) => setFormData({ ...formData, maxWeightKg: e.target.value ? Number(e.target.value) : undefined })}
                    />
                    </div>
                    <div>
                    <Label htmlFor="maxStackWeightKg">Peso máx encima (kg)</Label>
                    <Input
                        id="maxStackWeightKg"
                        type="number"
                        value={formData.maxStackWeightKg ?? ''}
                        onChange={(e) => setFormData({ ...formData, maxStackWeightKg: e.target.value ? Number(e.target.value) : undefined })}
                    />
                    <p className="text-xs text-slate-400 mt-1">Cuánto soporta apilado</p>
                    </div>
                    <div>
                    <Label htmlFor="costEur">Coste (€)</Label>
                    <Input
                        id="costEur"
                        type="number"
                        step="0.01"
                        value={formData.costEur ?? ''}
                        onChange={(e) => setFormData({ ...formData, costEur: e.target.value ? Number(e.target.value) : undefined })}
                    />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                    <Checkbox
                        id="requiresSeparatorAbove"
                        checked={formData.requiresSeparatorAbove ?? false}
                        onCheckedChange={(checked) => setFormData({ ...formData, requiresSeparatorAbove: checked === true })}
                    />
                    <Label htmlFor="requiresSeparatorAbove" className="cursor-pointer">
                        Requiere separador encima
                    </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                    <Checkbox
                        id="requiresSeparatorBelow"
                        checked={formData.requiresSeparatorBelow ?? false}
                        onCheckedChange={(checked) => setFormData({ ...formData, requiresSeparatorBelow: checked === true })}
                    />
                    <Label htmlFor="requiresSeparatorBelow" className="cursor-pointer">
                        Requiere separador debajo
                    </Label>
                    </div>
                </div>
                </div>
            </form>
          ) : (
            <div className="space-y-6 py-4">
                <div className="bg-slate-50 p-4 rounded-lg flex flex-col items-center justify-center border border-dashed border-slate-300 min-h-[150px]">
                    {currentModelPath ? (
                        <div className="text-center">
                            <Box className="h-12 w-12 text-green-600 mx-auto mb-2" />
                            <p className="text-sm font-medium text-green-700">Modelo Cargado</p>
                            <p className="text-xs text-slate-500 mt-1 truncate max-w-[200px]">{currentModelPath}</p>
                        </div>
                    ) : (
                        <div className="text-center text-slate-500">
                            <Box className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>No hay modelo 3D asignado</p>
                        </div>
                    )}
                </div>

                <div className="flex justify-center">
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        className="hidden" 
                        accept=".glb,.gltf" 
                        onChange={handleFileChange} 
                    />
                    <Button 
                        type="button" 
                        variant="secondary"
                        onClick={handleUploadClick}
                        disabled={isUploading}
                    >
                        {isUploading ? 'Subiendo...' : currentModelPath ? 'Reemplazar Modelo' : 'Subir Modelo (.glb)'}
                    </Button>
                </div>
                
                <p className="text-xs text-center text-slate-500">
                    Sube un archivo .glb o .gltf para visualizar este envase en el constructor 3D.
                </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
              {activeTab === 'model' ? 'Cerrar' : 'Cancelar'}
            </Button>
            {activeTab === 'general' && (
                <Button type="submit" form="packaging-form">Guardar</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
